import diff from 'microdiff';
import type { RawData, StoredProfile } from './types.ts';

interface ComparedRawData {
  profile: StoredProfile;
  rawData: RawData;
}

interface PathValue {
  present: boolean;
  value?: unknown;
}

type DifferencePath = ReadonlyArray<string | number>;

const storedProfiles: readonly StoredProfile[] = ['A', 'B', 'C'];

function pathStartsWith(path: DifferencePath, prefix: DifferencePath): boolean {
  if (path.length < prefix.length) {
    return false;
  }
  return prefix.every((part, index) => path[index] == part);
}

function differencePaths(
  profiles: readonly ComparedRawData[],
): DifferencePath[] {
  const paths = new Map<string, DifferencePath>();

  for (let leftIndex = 0; leftIndex < profiles.length; leftIndex += 1) {
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < profiles.length;
      rightIndex += 1
    ) {
      const left = profiles[leftIndex];
      const right = profiles[rightIndex];
      if (!left || !right) {
        continue;
      }
      for (const difference of diff(left.rawData, right.rawData, {
        cyclesFix: false,
      })) {
        const path = [...difference.path];
        paths.set(JSON.stringify(path), path);
      }
    }
  }

  const allPaths = [...paths.values()];
  return allPaths
    .filter(
      (path) =>
        !allPaths.some(
          (candidate) =>
            candidate.length > path.length && pathStartsWith(candidate, path),
        ),
    )
    .sort((left, right) => formatPath(left).localeCompare(formatPath(right)));
}

function pathValue(rawData: RawData, path: DifferencePath): PathValue {
  let value: unknown = rawData;
  for (const part of path) {
    if (value == null || typeof value != 'object') {
      return { present: false };
    }
    if (!Object.hasOwn(value, part)) {
      return { present: false };
    }
    value = (value as Record<string | number, unknown>)[part];
  }
  return { present: true, value };
}

function formatPath(path: DifferencePath): string {
  return path
    .map((part, index) => {
      if (typeof part == 'number') {
        return `[${part}]`;
      }
      if (/^[A-Za-z_$][\w$]*$/.test(part)) {
        return index == 0 ? part : `.${part}`;
      }
      return `[${JSON.stringify(part)}]`;
    })
    .join('');
}

function formatValue(value: unknown): string {
  if (typeof value == 'number' && !Number.isFinite(value)) {
    return String(value);
  }
  if (value === undefined) {
    return 'undefined';
  }
  return JSON.stringify(value) ?? String(value);
}

function valuesDiffer(values: readonly unknown[]): boolean {
  const first = values[0];
  return values
    .slice(1)
    .some(
      (value) =>
        diff({ value: first }, { value }, { cyclesFix: false }).length != 0,
    );
}

export function compareRawData(
  rawDataByProfile: Readonly<Partial<Record<StoredProfile, RawData>>>,
): string[] {
  const profiles = storedProfiles.flatMap((profile) => {
    const rawData = rawDataByProfile[profile];
    return rawData ? [{ profile, rawData }] : [];
  });
  if (profiles.length < 2) {
    return [];
  }

  const messages: string[] = [];
  for (const path of differencePaths(profiles)) {
    const formattedPath = formatPath(path);
    const values = profiles.map(({ profile, rawData }) => ({
      profile,
      ...pathValue(rawData, path),
    }));
    for (const value of values) {
      if (!value.present) {
        messages.push(
          `Profile ${value.profile} doesn't contain ${formattedPath}`,
        );
      }
    }

    const presentValues = values.filter((value) => value.present);
    const hasMissingValue = presentValues.length != values.length;
    if (
      presentValues.length > 0 &&
      (hasMissingValue ||
        valuesDiffer(presentValues.map((value) => value.value)))
    ) {
      messages.push(
        `${formattedPath}: ${presentValues
          .map(
            (value) => `profile ${value.profile} = ${formatValue(value.value)}`,
          )
          .join('; ')}`,
      );
    }
  }
  return messages;
}
